import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  Dispatch,
  SetStateAction,
  useCallback,
} from 'react';
import { ContractStatusFilterOption } from '../../types';

export interface ContractStatusOption {
  slug: string;
  displayValue: string;
}

interface ContractsListFiltersContextValue {
  searchQuery: string | undefined;
  setSearchQuery: Dispatch<SetStateAction<string | undefined>>;
  moveInStartDate: Date | undefined;
  moveInEndDate: Date | undefined;
  moveOutStartDate: Date | undefined;
  moveOutEndDate: Date | undefined;
  setMoveInDateRange: (dateRange: [Date | null, Date | null]) => void;
  setMoveOutDateRange: (dateRange: [Date | null, Date | null]) => void;
  residencyRegistrationStatus: ContractStatusOption | undefined;
  setResidencyRegistrationStatus: Dispatch<SetStateAction<ContractStatusOption | undefined>>;
  propertyManagerId: number | undefined;
  setPropertyManagerId: Dispatch<SetStateAction<number | undefined>>;
  status: ContractStatusFilterOption | undefined;
  setStatus: Dispatch<SetStateAction<ContractStatusFilterOption | undefined>>;
  roomNumber: string | undefined;
  setRoomNumber: Dispatch<SetStateAction<string | undefined>>;
}

interface ContractsListFiltersProviderProps {
  children: React.ReactNode;
}

const ContractsListFiltersContext = createContext<ContractsListFiltersContextValue | undefined>(
  undefined
);

export const ContractsListFiltersProvider: React.FC<ContractsListFiltersProviderProps> = ({
  children,
}) => {
  const [searchQuery, setSearchQuery] = useState<string>();
  const [moveInStartDate, setMoveInStartDate] = useState<Date>();
  const [moveInEndDate, setMoveInEndDate] = useState<Date>();
  const [moveOutStartDate, setMoveOutStartDate] = useState<Date>();
  const [moveOutEndDate, setMoveOutEndDate] = useState<Date>();
  const [residencyRegistrationStatus, setResidencyRegistrationStatus] =
    useState<ContractStatusOption>();
  const [propertyManagerId, setPropertyManagerId] = useState<number>();
  const [status, setStatus] = useState<ContractStatusFilterOption>();

  const setMoveInDateRange = useCallback((dateRange: [Date | null, Date | null]) => {
    setMoveInStartDate(dateRange[0] ?? undefined);
    setMoveInEndDate(dateRange[1] ?? undefined);
  }, []);

  const setMoveOutDateRange = useCallback((dateRange: [Date | null, Date | null]) => {
    setMoveOutStartDate(dateRange[0] ?? undefined);
    setMoveOutEndDate(dateRange[1] ?? undefined);
  }, []);
  const [roomNumber, setRoomNumber] = useState<string>();

  const value = React.useMemo(
    () => ({
      searchQuery,
      setSearchQuery,
      moveInStartDate,
      moveInEndDate,
      moveOutStartDate,
      moveOutEndDate,
      setMoveInDateRange,
      setMoveOutDateRange,
      residencyRegistrationStatus,
      setResidencyRegistrationStatus,
      propertyManagerId,
      setPropertyManagerId,
      status,
      setStatus,
      roomNumber,
      setRoomNumber,
    }),
    [
      searchQuery,
      moveInStartDate,
      moveInEndDate,
      moveOutStartDate,
      moveOutEndDate,
      setMoveInDateRange,
      setMoveOutDateRange,
      residencyRegistrationStatus,
      propertyManagerId,
      status,
      roomNumber
    ]
  );

  return (
    <ContractsListFiltersContext.Provider value={value}>
      {children}
    </ContractsListFiltersContext.Provider>
  );
};

export const useContractsListFiltersContext = () => {
  const context = useContext(ContractsListFiltersContext);
  if (context === undefined) {
    throw new Error(
      'useContractsListFiltersContext must be used within a ContractsListFiltersProvider'
    );
  }
  return context;
};
